import { useState } from 'react';

import { Accordion } from '@iwoca/orion';
import { plural, t } from '@lingui/core/macro';
import { clsx } from 'clsx';

import styles from './BankStatementsOpenBanking.module.css';
import { useGetContext } from '../../../../api/lendingApi/lendingApiQueries';
import { useLocale } from '../../../../hooks/useLocale';
import { useSignupParams } from '../../../../hooks/useSignupParams';
import { useSplitTestContext } from '../../../../hooks/useSplitTestContext';
import {
  TOfferDataRequirement,
  TTrueLayerAlertStatus,
} from '../../../../types';
import Required from '../../DocumentRequirement/Required';
import OpenBanking from '../../OpenBanking/OpenBanking';
import { getLinkedBankAccountDetails } from '../../OpenBanking/trueLayer.helpers';
import TrueLayerAlertBox from '../../OpenBanking/TrueLayerAlertBox/TrueLayerAlertBox';
import TrueLayerLinkedAccount from '../../OpenBanking/TrueLayerLinkedAccount/TrueLayerLinkedAccount';
import TrueLayerShareableLink from '../../OpenBanking/TrueLayerShareableLink/TrueLayerShareableLink';
import ManualUploadDE from '../../TradingHistoryDE/RequirementTypes/BankStatementsDE/ManualUploadDE';
import ManualUploadUK from '../../TradingHistoryUK/RequirementTypes/BankStatements/ManualUpload';

type TProps = {
  quantifier: number;
  status?: TOfferDataRequirement['actions'][number]['status'];
  isSubmitted: boolean;
  resolutionMethods: TOfferDataRequirement['actions'][number]['resolution_methods'];
};

const BankStatementOpenBanking = ({
  quantifier,
  status,
  isSubmitted,
  resolutionMethods,
}: TProps) => {
  const [trueLayerAlertStatus, setTrueLayerAlertStatus] =
    useState<TTrueLayerAlertStatus>(null);

  const { context } = useGetContext();
  const { isUK, isDE } = useLocale();
  const { isDirectCustomer } = useSignupParams();
  const { hasOpenBankingShareableLinkEnabled } = useSplitTestContext();

  const renderTitle = () => {
    if (isDE) {
      return plural(quantifier, {
        one: 'Business bank statements for the last # month',
        other: 'Business bank statements for the last # months',
      });
    }

    return `Business bank statements for the last ${
      quantifier > 1 ? `${quantifier} months` : '1 month'
    } (from today)`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        {/* id has space instead of hyphen to match other dynamic ids used for scrolling to requirements */}
        <h4
          className={clsx(styles.title, styles.titleWithSubtitle)}
          id="document-requirement-bank statement"
        >
          {renderTitle()}
          {isSubmitted && (status === 'required' || status === 'error') && (
            <Required />
          )}
        </h4>
      </div>

      <OpenBanking setAlertStatus={setTrueLayerAlertStatus} />
      {isUK && isDirectCustomer && hasOpenBankingShareableLinkEnabled && (
        <TrueLayerShareableLink />
      )}
      <Accordion
        title={t`My bank is not available to link`}
        small
        variant="secondary"
      >
        {isDE ? (
          <ManualUploadDE
            title={renderTitle()}
            status={status}
            isSubmitted={isSubmitted}
          />
        ) : (
          <ManualUploadUK
            title={renderTitle()}
            status={status}
            isSubmitted={isSubmitted}
          />
        )}
      </Accordion>
      {trueLayerAlertStatus && (
        <TrueLayerAlertBox status={trueLayerAlertStatus} />
      )}
      <TrueLayerLinkedAccount
        linkedBankAccount={getLinkedBankAccountDetails(
          resolutionMethods,
          context,
        )}
      />
    </div>
  );
};

export default BankStatementOpenBanking;
